import { useEffect, useRef, useState } from "react";
import { areRouteNamespacesLoaded, preloadNamespacesForRoute } from "../i18n";

/**
 * Bounded i18n readiness for authenticated app entry.
 * This gates only the first authenticated shell + route render.
 * Later route changes warm namespaces without replacing the mounted app shell.
 */
export default function useRouteNamespacesReady(pathname, requirePreload) {
  const initialReady = !requirePreload || !pathname || areRouteNamespacesLoaded(pathname);
  const hasCompletedInitialGateRef = useRef(requirePreload && !!pathname && initialReady);
  const [ready, setReady] = useState(initialReady);

  useEffect(() => {
    if (!requirePreload || !pathname) {
      hasCompletedInitialGateRef.current = false;
      setReady(true);
      return undefined;
    }

    if (hasCompletedInitialGateRef.current) {
      setReady(true);
      void preloadNamespacesForRoute(pathname);
      return undefined;
    }

    if (areRouteNamespacesLoaded(pathname)) {
      hasCompletedInitialGateRef.current = true;
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    setReady(false);

    preloadNamespacesForRoute(pathname).then(() => {
      if (!cancelled) {
        hasCompletedInitialGateRef.current = true;
        setReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, requirePreload]);

  return ready;
}
